// The right-hand workflow column: "Recommended Next Steps" driven by real
// account state, and "Quick Links" driven by where the user is in the trial
// workflow. Rendered once by AppLayout (src/App.tsx) for authenticated,
// non-marketing routes.
//
// Layout: a sticky 320px column at `xl` and up; below that it stacks under the
// page content in normal flow, so nothing overlaps on a laptop or phone.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Compass,
} from 'lucide-react';
import { trackEvent } from '@/utils/analytics';
import { useWorkflowSnapshot } from '@/hooks/useWorkflowSnapshot';
import {
  ROUTE_META,
  STAGE_BY_ID,
  labelForPath,
  resolveRouteKey,
} from '@/content/workflowMap';
import { getRecommendedSteps, withCase, type RecommendedStep } from '@/lib/recommendedSteps';

const COLLAPSE_KEY = 'sentantia_workflow_rail_collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
}

// ─── Presentational pieces ───────────────────────────────────────────────────

/** The small uppercase label used for every section eyebrow in the rail. */
const EYEBROW = 'text-[11px] font-semibold text-paper-500 uppercase tracking-wider';

const TONE_ACCENT: Record<RecommendedStep['tone'], string> = {
  default: 'border-l-paper-300 group-hover:border-l-forest-500',
  progress: 'border-l-forest-500',
  urgent: 'border-l-amber-500',
};

function StepItem({
  step,
  index,
  onNavigate,
}: {
  step: RecommendedStep;
  index: number;
  onNavigate: (step: RecommendedStep) => void;
}) {
  return (
    <li>
      <Link
        to={step.to}
        onClick={() => onNavigate(step)}
        className={`group block border-l-2 pl-3 py-2 transition-colors hover:bg-paper-100 ${TONE_ACCENT[step.tone]}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-navy-900 group-hover:text-forest-600 transition-colors">
            {step.label}
          </span>
          <span className="flex items-center gap-1 shrink-0 pt-0.5">
            {step.tone === 'progress' && (
              <span
                className="w-1.5 h-1.5 rounded-full bg-forest-500 animate-pulse"
                aria-hidden="true"
              />
            )}
            <ArrowRight
              className="w-3.5 h-3.5 text-paper-400 group-hover:text-forest-600 transition-colors"
              aria-hidden="true"
            />
          </span>
        </div>
        <p className="text-xs text-paper-600 mt-0.5 leading-snug">{step.why}</p>
        <span className="sr-only">Recommended step {index + 1}</span>
      </Link>
    </li>
  );
}

function QuickLinkItem({
  path,
  href,
  onNavigate,
}: {
  path: string;
  href: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <li>
      <Link
        to={href}
        onClick={() => onNavigate(path)}
        className="group flex items-center justify-between gap-2 py-1.5 text-sm text-navy-800 hover:text-forest-600 transition-colors"
      >
        <span className="truncate">{labelForPath(path)}</span>
        <ChevronRight
          className="w-3 h-3 shrink-0 text-paper-400 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

// ─── Rail ────────────────────────────────────────────────────────────────────

export interface WorkflowRailProps {
  /**
   * Pixels from the top of the viewport the sticky column should park at:
   * the fixed TopNav, plus the demo banner when it is showing. Passed in
   * because only AppLayout knows whether the banner is there.
   */
  stickyTop?: number;
}

export default function WorkflowRail({ stickyTop = 64 }: WorkflowRailProps) {
  const location = useLocation();
  const snapshot = useWorkflowSnapshot();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const routeKey = useMemo(() => resolveRouteKey(location.pathname), [location.pathname]);
  const meta = routeKey ? ROUTE_META[routeKey] : undefined;
  const stage = meta ? STAGE_BY_ID[meta.stage] : undefined;

  const steps = useMemo(
    () => getRecommendedSteps(routeKey, snapshot),
    [routeKey, snapshot]
  );

  const activeCaseId = snapshot.activeCase?.id;

  /**
   * Quick links: the workflow neighbours of this page, and nothing else.
   *
   * Two earlier shapes were removed as noise. Grouping by workflow stage
   * produced a column of one-item headings, since a page's neighbours usually
   * span four or five stages. A standing "Anywhere" block (Dashboard, Cases,
   * Simulation History) plus a Help Center / Support footer just restated
   * links the TopNav and Footer already carry on every page. For the same
   * reason the route map no longer lists /dashboard or /cases as anyone's
   * neighbour, and /help and /support only inside the help and legal pages,
   * where they are the actual workflow rather than chrome.
   *
   * Links that read `?case=` carry the active case so context survives the
   * jump. The page you are already on is never listed.
   */
  const quickLinks = useMemo(() => {
    const seen = new Set<string>(routeKey ? [routeKey] : []);
    const out: { path: string; href: string }[] = [];
    for (const path of meta?.quickLinks ?? []) {
      if (seen.has(path)) continue;
      if (!ROUTE_META[path]) continue; // guarded by workflowMap.test.ts
      seen.add(path);
      out.push({ path, href: withCase(path, activeCaseId) });
    }
    return out;
  }, [meta, routeKey, activeCaseId]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // Storage can be unavailable (private mode). The toggle still works
        // for this session; it just will not be remembered.
      }
      trackEvent('workflow_rail_toggled', { collapsed: next, page: routeKey ?? 'unknown' });
      return next;
    });
  }, [routeKey]);

  useEffect(() => {
    if (!routeKey) return;
    trackEvent('workflow_rail_shown', {
      page: routeKey,
      stage: meta?.stage ?? 'unknown',
      stepCount: steps.length,
    });
    // Fires on route change, not on every reactive data tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  const handleStepClick = useCallback(
    (step: RecommendedStep) => {
      trackEvent('workflow_rail_step_clicked', {
        step: step.key,
        tone: step.tone,
        from: routeKey ?? 'unknown',
        to: step.to,
      });
    },
    [routeKey]
  );

  const handleQuickLinkClick = useCallback(
    (path: string) => {
      trackEvent('workflow_rail_quicklink_clicked', { from: routeKey ?? 'unknown', to: path });
    },
    [routeKey]
  );

  // A page with no entry in the workflow map gets no rail rather than a
  // guessed one.
  if (!routeKey) return null;

  if (collapsed) {
    return (
      <aside className="xl:w-10 xl:shrink-0 border-t xl:border-t-0 xl:border-l border-paper-300 bg-white">
        <div
          style={{ top: stickyTop }}
          className="xl:sticky flex xl:flex-col items-center justify-center xl:justify-start gap-2 p-2 xl:py-4"
        >
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex items-center gap-2 text-paper-500 hover:text-navy-900 transition-colors p-1.5"
            aria-label="Show workflow sidebar"
            aria-expanded={false}
          >
            <ChevronLeft className="w-4 h-4 hidden xl:block" aria-hidden="true" />
            <ChevronDown className="w-4 h-4 xl:hidden" aria-hidden="true" />
            <span className="text-sm xl:hidden">Show next steps</span>
          </button>
          <span
            className={`hidden xl:block ${EYEBROW} whitespace-nowrap [writing-mode:vertical-rl]`}
            aria-hidden="true"
          >
            Next Steps
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Workflow sidebar"
      className="w-full xl:w-[320px] xl:shrink-0 border-t xl:border-t-0 xl:border-l border-paper-300 bg-white"
    >
      <div
        style={{ top: stickyTop, ['--rail-top' as string]: `${stickyTop}px` }}
        className="xl:sticky xl:max-h-[calc(100vh-var(--rail-top))] xl:overflow-y-auto p-5 space-y-6"
      >
        {/* Header: where you are in the workflow */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0" role="group" aria-label="Current stage">
            <div className={`${EYEBROW} inline-flex items-center gap-1.5`}>
              <Compass className="w-3 h-3" aria-hidden="true" />
              {stage?.label ?? 'Workflow'}
            </div>
            <p className="text-xs text-paper-600 mt-1 leading-snug">{stage?.blurb}</p>
          </div>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="shrink-0 p-1 text-paper-400 hover:text-navy-900 transition-colors"
            aria-label="Hide workflow sidebar"
            aria-expanded
          >
            <ChevronRight className="w-4 h-4 hidden xl:block" aria-hidden="true" />
            <ChevronDown className="w-4 h-4 xl:hidden" aria-hidden="true" />
          </button>
        </div>

        {/* Which case these recommendations are about */}
        {snapshot.activeCase && (
          <Link
            to={`/cases/${snapshot.activeCase.id}`}
            onClick={() => handleQuickLinkClick('/cases/:caseId')}
            className="group flex items-start gap-2 border border-paper-300 bg-paper-100 p-3 hover:border-forest-500 transition-colors"
          >
            <Briefcase className="w-3.5 h-3.5 text-paper-500 mt-0.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              <span className={`block ${EYEBROW}`}>Active case</span>
              <span className="block text-sm font-medium text-navy-900 group-hover:text-forest-600 transition-colors truncate">
                {snapshot.activeCase.name}
              </span>
            </span>
          </Link>
        )}

        {/* Stacked under the page below `xl`, the rail is full-bleed, so the
            two sections sit side by side there instead of running as one
            narrow column down the middle of a wide empty band. */}
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-1">
        {/* ─── Recommended Next Steps ─────────────────────────────────────── */}
        <section aria-labelledby="workflow-rail-steps">
          <h2
            id="workflow-rail-steps"
            className="text-sm font-semibold text-navy-900 mb-3"
          >
            Recommended Next Steps
          </h2>
          {snapshot.loading ? (
            <div className="space-y-3" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse space-y-1.5 pl-3 border-l-2 border-l-paper-200">
                  <div className="h-3.5 bg-paper-200 w-3/4" />
                  <div className="h-3 bg-paper-200 w-full" />
                </div>
              ))}
            </div>
          ) : steps.length === 0 ? (
            <p className="text-xs text-paper-600 leading-snug">
              Nothing is waiting on you here. Use the quick links below to keep moving.
            </p>
          ) : (
            <ol className="space-y-1 -ml-px">
              {steps.map((step, i) => (
                <StepItem key={step.key} step={step} index={i} onNavigate={handleStepClick} />
              ))}
            </ol>
          )}
        </section>

        {/* ─── Quick Links ────────────────────────────────────────────────── */}
        {/* The rule above the section only makes sense when the two sections
            are stacked in the narrow column. */}
        <section
          aria-labelledby="workflow-rail-links"
          className="xl:border-t xl:border-paper-300 xl:pt-5"
        >
          <h2 id="workflow-rail-links" className="text-sm font-semibold text-navy-900 mb-3">
            Quick Links
          </h2>
          <ul>
            {quickLinks.map(({ path, href }) => (
              <QuickLinkItem
                key={path}
                path={path}
                href={href}
                onNavigate={handleQuickLinkClick}
              />
            ))}
          </ul>
        </section>
        </div>
      </div>
    </aside>
  );
}
